import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentTypes } from 'constants/enums/documentTypes'
import { IDocuments } from 'interfaces/i-documents'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import documentServices from 'services/documentServices'

export interface DocumentEntry {
	document: IDocuments
	blob: Blob
}

export type RequiredDocumentsMap = Partial<Record<DocumentTypes, DocumentEntry>>

export function useRequiredDocuments(requiredTypes: DocumentTypes[]): {
	loading: boolean
	error: boolean
	allDocumentsLoaded: boolean
	documents: RequiredDocumentsMap
} {
	const { t } = useTranslation()
	const { loanApplication }: IApplicationContext = useContext(ApplicationDispatchContext)
	const applicationId = loanApplication.applicationData?.loanNumber!

	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(false)
	const [documents, setDocuments] = useState<RequiredDocumentsMap>({})

	const allDocumentsLoaded = requiredTypes.every((type) => documents[type] !== undefined)

	const loadDocuments = useCallback(async () => {
		setLoading(true)
		try {
			const response = await documentServices.getDownloadDocuments(
				applicationId.toString(),
				t,
			)
			loanApplication.setDocumentData(response)

			const loaded: RequiredDocumentsMap = {}
			for (const doc of response) {
				if (requiredTypes.includes(doc.type as DocumentTypes)) {
					const { blob: blobWithoutType }: any =
						await documentServices.getDownloadDocumentById(String(doc.documentId))
					loaded[doc.type as DocumentTypes] = {
						document: doc,
						blob: new Blob([blobWithoutType], { type: 'application/pdf' }),
					}
				}
			}
			setDocuments(loaded)
		} catch (err) {
			console.warn(err)
			setError(true)
		} finally {
			setLoading(false)
		}
		// requiredTypes is intentionally excluded — it's static config passed at call site
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationId, loanApplication, t])

	useEffect(() => {
		loadDocuments()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return { loading, error, allDocumentsLoaded, documents }
}
